import React from 'react';
import { MockVideo } from '@/hooks/video-generator/useVideoGeneration';
import { Separator } from '@/components/ui/separator';
import { VideoSettings } from './VideoSettings';
import { ChevronLeft, ChevronRight, CloudDownload, Trash2, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import GradientButton from '@/components/ui/GradientButton';
import StorageButton from '@/components/universal-storage/StorageButton';
import GlassPill from '@/components/ui/GlassPill';

interface VideoMetadataSidebarProps {
  video: MockVideo;
  isOwner: boolean;
  isDeleteLoading: boolean;
  onDownload: () => void;
  onDelete: () => void;
  onLike: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  isLoadingMore?: boolean;
  hasNextPage?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onLoadMore?: () => void;
}

export const VideoMetadataSidebar: React.FC<VideoMetadataSidebarProps> = ({
  video,
  isOwner,
  isDeleteLoading,
  onDownload,
  onDelete,
  onLike,
  hasPrevious = false,
  hasNext = false,
  isLoadingMore = false,
  hasNextPage = false,
  onPrevious,
  onNext,
  onLoadMore
}) => {
  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full lg:w-[250px] flex-shrink-0 lg:border-l border-accent/10 flex flex-col md:h-full md:max-h-[95vh]">
      {/* Main content container with proper structure */}
      <div className="flex flex-col md:h-full">
        {/* Navigation buttons at the top */}
        <div className="border-b border-accent/10 bg-background">
          <div className="p-3 md:p-4">
            <div className="flex justify-between items-center gap-3 w-full">
              <motion.button
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                  hasPrevious
                    ? "bg-gradient-to-r from-purple-100/80 to-pink-100/80 dark:from-purple-800/80 dark:to-pink-800/80 border-purple-200/50 dark:border-purple-700/50 text-purple-700 dark:text-purple-200 hover:from-purple-200/90 hover:to-pink-200/90 dark:hover:from-purple-700/90 dark:hover:to-pink-700/90 shadow-sm hover:shadow-md"
                    : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                )}
                onClick={hasPrevious && onPrevious ? onPrevious : undefined}
                disabled={!hasPrevious}
                whileHover={hasPrevious ? { scale: 1.02, y: -1 } : {}}
                whileTap={hasPrevious ? { scale: 0.98 } : {}}
                data-test-id="sidebar-prev-button"
              >
                <ChevronLeft size={16} className="flex-shrink-0" />
                <span>Last</span>
              </motion.button>
              
              <motion.button
                className={cn(
                  "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                  hasNext
                    ? "bg-gradient-to-r from-purple-800 to-pink-800 dark:from-purple-700 dark:to-pink-700 border-purple-700/50 dark:border-purple-600/50 text-white hover:from-purple-700 hover:to-pink-700 dark:hover:from-purple-600 dark:hover:to-pink-600 shadow-sm hover:shadow-md"
                    : isLoadingMore
                      ? "bg-gradient-to-r from-purple-700 to-pink-700 dark:from-purple-600 dark:to-pink-600 border-purple-600/50 dark:border-purple-500/50 text-white shadow-sm"
                      : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                )}
                onClick={() => {
                  // Preserve sophisticated navigation logic
                  if (hasNext && onNext && !isLoadingMore) {
                    onNext();
                  } 
                  else if (hasNextPage && !isLoadingMore && onLoadMore) {
                    onLoadMore();
                  }
                }}
                disabled={(!hasNext && !hasNextPage) || isLoadingMore}
                whileHover={hasNext && !isLoadingMore ? { scale: 1.02, y: -1 } : {}}
                whileTap={hasNext && !isLoadingMore ? { scale: 0.98 } : {}}
                data-test-id="sidebar-next-button"
                type="button"
              >
                {isLoadingMore ? (
                  <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent animate-pulse">
                    Loading...
                  </span>
                ) : (
                  <>
                    <span>Next</span>
                    <ChevronRight size={16} className="flex-shrink-0" />
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Storage Button - Right below navigation */}
        <div className="border-b border-accent/10 bg-background">
          <div className="p-3 md:p-4">
            <StorageButton 
              itemId={video.id}
              contentType="image"
              variant="compact"
              gradientFrom="from-purple-50/80 to-pink-50/80 dark:from-purple-950/30 dark:to-pink-950/30"
              gradientTo="hover:from-purple-100/80 hover:to-pink-100/80 dark:hover:from-purple-900/40 dark:hover:to-pink-900/40"
              accentColor="from-purple-400 to-pink-500"
              iconColor="text-purple-600 dark:text-purple-400"
              borderColor="border-purple-200/60 dark:border-purple-700/40"
              hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
              addText="Save Video"
              manageText="Manage Video"
              addDescription="Add to your storage"
              manageDescription="Video saved"
              dropdownLabel="Save to Storage"
            />
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="p-3 md:p-4 md:overflow-y-auto">
          
          
          <Separator className="my-4" />
          
          {/* Delete button only for owners */}
          {isOwner && (
            <div className="mt-3">
              <GradientButton
                variant="red"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onDelete}
                isLoading={isDeleteLoading}
                disabled={isDeleteLoading}
                testId="delete-video-button"
                fullWidth
              >
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </GradientButton>
            </div>
          )}
        </div>
        
        {/* Fixed bottom section with download button */}
        <div className="border-t border-accent/10 bg-background md:mt-auto">
          <div className="p-3 md:p-4">
            <GradientButton
              variant="blue"
              icon={<CloudDownload className="h-4 w-4" />}
              onClick={onDownload}
              testId="download-video-button"
              fullWidth
            >
              Download
            </GradientButton>
          </div>
        </div>
      </div>
    </div>
  );
}; 